import {
  type FC,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react';
import {
  IoIosArrowDown,
  IoIosArrowUp,
  IoIosCheckbox,
  IoIosClose,
} from 'react-icons/io';
import { LoadingSpinner } from '@components/loadingSpinner';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import { SelectedItem, SelectedOptionsContainer } from './styled';
import { type DropdownOptions } from '../SelectInput/MultiSelectInput/MultiSelectInput';
import { arraysAreEqual } from 'utils/arraysAreEqual';
import { FErrorLabel } from '@components/form/FErrorLabel';
import { type FieldError } from 'react-hook-form';
import { addInputErrorClasses } from 'utils/handleFormErrorStyle';
import { useTranslation } from 'react-i18next';
import { MdCheckBoxOutlineBlank } from 'react-icons/md';
import { createRoot } from 'react-dom/client';
import SearchContainer from './SearchContainer';
import useClickOutside from 'hooks/useOutsideClick/useOutsideClick';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type FetchNextPageOptions,
  type InfiniteQueryObserverResult,
  useQueryClient,
} from '@tanstack/react-query';

export interface MultiComboBoxInputProps {
  query: string;
  handleQuery: (value: string) => void;
  options: DropdownOptions[];
  value?: DropdownOptions[] | DropdownOptions;
  placeholder?: string;
  isFetching: boolean;
  onChange?:
    | ((value: DropdownOptions[]) => void)
    | ((value: DropdownOptions) => void);
  initialValues?: DropdownOptions[] | DropdownOptions;
  error?: FieldError | undefined;
  'data-testid'?: string;
  selectedOptionsContainerClasses?: string;
  removeRelativeContainer?: boolean;
  disableSelectedList?: boolean;
  maxSelected?: number;
  disabled?: boolean;
  selectAll?: boolean;
  id?: string;
  showSearchIcon?: boolean;
  multiple?: boolean;
  maxHeight?: number;
  renderCustomOption?: (options?: DropdownOptions) => ReactNode;
  // Infinite scrolling props - matching Table component pattern
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<unknown, unknown>>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // Query keys to reset when component unmounts
  infiniteQueryKeys?: string[];
}

const compareValues = (a: DropdownOptions, b: DropdownOptions): boolean => {
  return a?.value === b?.value;
};

const MultiComboBoxInput: FC<MultiComboBoxInputProps> = forwardRef(
  (
    {
      query,
      handleQuery,
      options,
      placeholder = '',
      isFetching,
      value,
      onChange,
      error,
      initialValues,
      'data-testid': testid,
      selectedOptionsContainerClasses,
      removeRelativeContainer = false,
      disableSelectedList = false,
      maxSelected,
      disabled = false,
      selectAll = false,
      showSearchIcon = true,
      multiple = true,
      maxHeight = 240,
      id,
      renderCustomOption,
      // Infinite scrolling props
      fetchNextPage,
      hasNextPage = false,
      isFetchingNextPage = false,
      infiniteQueryKeys,
    },
    ref
  ) => {
    const [selectedList, setSelected] = useState<DropdownOptions[]>([]);
    const [areAllSelected, setAreAllSelected] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [comboboxRef, setComboboxRef] = useState<Element | null>(null);
    const dropdownRef = useRef(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const isUserInteraction = useRef(false);
    const hasUserModified = useRef(false);
    const interactionId = useRef(0); // Unique ID for each user interaction
    const lastProcessedInteractionId = useRef(0); // Track last processed interaction
    const previousInitialValues = useRef<
      DropdownOptions[] | DropdownOptions | undefined
    >(initialValues);

    const { theme } = useTheme();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    // Safe comparison function for initialValues
    const hasInitialValuesChanged = (
      prev: DropdownOptions[] | DropdownOptions | undefined,
      current: DropdownOptions[] | DropdownOptions | undefined
    ): boolean => {
      if (prev === current) return false;
      if (prev === undefined || current === undefined) return true;

      try {
        // For simple comparison, stringify and compare
        return JSON.stringify(prev) !== JSON.stringify(current);
      } catch (error) {
        // Fallback to reference comparison if stringify fails
        return prev !== current;
      }
    };

    const parsedOptions = useMemo(() => {
      if (multiple && selectAll && options?.length > 0) {
        return [{ value: 'selectAll', name: 'Select all results' }, ...options];
      }
      return options;
    }, [multiple, selectAll, options]);

    const handleOutsideClick = (): void => {
      setIsOpen(false);
      // Reset scroll position when closing to prevent scroll animation on next open
      if (comboboxRef) {
        const scrollElement = comboboxRef as HTMLElement;
        if (scrollElement) {
          // Use requestAnimationFrame to ensure this happens after state updates
          requestAnimationFrame(() => {
            scrollElement.scrollTop = 0;
          });
        }
      }
    };

    useClickOutside(dropdownRef, handleOutsideClick);

    // Reset infinite queries when component unmounts to prevent
    // re-fetching all cached pages when reopening modals
    useEffect(() => {
      return () => {
        if (fetchNextPage && infiniteQueryKeys) {
          try {
            void queryClient.resetQueries({
              queryKey: infiniteQueryKeys,
              type: 'inactive',
            });
          } catch {
            console.warn('Failed to reset queries during component unmount');
          }
        }
      };
    }, [fetchNextPage, infiniteQueryKeys, queryClient]);

    useEffect(() => {
      if (!onChange || !isUserInteraction.current) {
        return;
      }

      // Prevent processing the same interaction multiple times
      if (lastProcessedInteractionId.current >= interactionId.current) {
        return; // Already processed this interaction
      }
      lastProcessedInteractionId.current = interactionId.current;

      if (multiple) {
        // Multi-selection logic
        if (selectAll) {
          // Filter 'selectAll' value
          const parsedList = selectedList.filter(
            (item) => item.value !== 'selectAll'
          );
          (onChange as (value: DropdownOptions[]) => void)(parsedList);
          return;
        }
        (onChange as (value: DropdownOptions[]) => void)(selectedList);
      } else {
        // Trigger onChange for single input
        if (selectedList?.length) {
          (onChange as (value: DropdownOptions) => void)(selectedList[0]);
        }
      }
      isUserInteraction.current = false;
    }, [selectedList, multiple, selectAll, onChange]);

    useEffect(() => {
      if (value !== undefined) {
        // Controlled component - use value prop (always sync)
        // For controlled components, always sync with value prop
        if (Array.isArray(value) && value.length === 0) {
          setSelected([]);
          // Reset areAllSelected when empty
          if (areAllSelected) {
            setAreAllSelected(false);
          }
        } else {
          const valueArray = Array.isArray(value)
            ? value
            : value
            ? [value]
            : [];

          if (
            areAllSelected &&
            selectAll &&
            multiple &&
            !valueArray.some((item) => item.value === 'selectAll')
          ) {
            const selectAllOption = {
              value: 'selectAll',
              name: 'Select all results',
            };
            setSelected([selectAllOption, ...valueArray]);
          } else {
            setSelected(valueArray);
          }
        }
        // Don't modify hasUserModified for controlled components
        // The parent component manages the state
      } else if (initialValues !== undefined) {
        // Uncontrolled component - use initialValues prop
        // Only update if user hasn't manually modified the selection
        if (!hasUserModified.current) {
          // Check if this is the first time or initialValues changed
          const valuesChanged = hasInitialValuesChanged(
            previousInitialValues.current,
            initialValues
          );

          if (previousInitialValues.current === undefined || valuesChanged) {
            setSelected(
              Array.isArray(initialValues)
                ? initialValues
                : initialValues
                ? [initialValues]
                : []
            );
            previousInitialValues.current = initialValues;
          }
        }
        // If user has modified, never override their changes
        // The component will unmount/remount when navigating to different data
      } else if (
        initialValues === undefined &&
        previousInitialValues.current !== undefined
      ) {
        // initialValues changed from defined to undefined (e.g., navigating to anomaly without responsible)
        setSelected([]);
        previousInitialValues.current = undefined;
        hasUserModified.current = false;
      }
    }, [value, initialValues]);

    const handleRemoveFromSelectedList = (id: number | string): void => {
      const updatedSelectedList = [...selectedList];
      const indexToRemove = updatedSelectedList.findIndex(
        (item) => item.value === id
      );

      if (indexToRemove !== -1) {
        updatedSelectedList.splice(indexToRemove, 1);

        if (areAllSelected) {
          setAreAllSelected(false);
          const selectAllIndex = updatedSelectedList.findIndex(
            (item) => item.value === 'selectAll'
          );
          if (selectAllIndex !== -1) {
            updatedSelectedList.splice(selectAllIndex, 1);
          }
        }
      }
      if (!arraysAreEqual(selectedList, updatedSelectedList)) {
        interactionId.current += 1;
        isUserInteraction.current = true;
        hasUserModified.current = true;
        setSelected(updatedSelectedList);
      }
    };

    const handleSelect = (list: DropdownOptions[] | DropdownOptions): void => {
      if (!multiple && !Array.isArray(list)) {
        const isAlreadySelected = selectedList.some(
          (selected) => selected.value === (list as DropdownOptions).value
        );

        interactionId.current += 1;
        isUserInteraction.current = true;
        hasUserModified.current = true;
        if (isAlreadySelected) {
          setSelected([]);
        } else {
          setSelected(list ? [list] : []);
        }

        setIsOpen(false);
        return;
      }

      if (!Array.isArray(list)) {
        return;
      }

      // if we select "selectAll"
      if (isSelectAllInList(list) && !areAllSelected) {
        setAreAllSelected(true);
        // TODO verify the results + selected options size are not > maxSelected
        list = addAllResultsToList(list);
      } // if we unselect "selectAll"
      else if (!isSelectAllInList(list) && areAllSelected) {
        list = removeAllResultsFromList(list);
        setAreAllSelected(false);
      }
      if (maxSelected && maxSelected < list.length) {
        return;
      }
      interactionId.current += 1;
      isUserInteraction.current = true;
      hasUserModified.current = true;
      setSelected(list);
    };

    const isSelectAllInList = (list: DropdownOptions[]): boolean => {
      return list.some((item) => item.value === 'selectAll');
    };

    const addAllResultsToList = (
      list: DropdownOptions[]
    ): DropdownOptions[] => {
      const combinedList = [...list, ...parsedOptions];
      const uniqueList = Array.from(
        new Map(combinedList.map((item) => [item.value, item])).values()
      );
      return uniqueList;
    };

    const removeAllResultsFromList = (
      list: DropdownOptions[]
    ): DropdownOptions[] => {
      const filteredList = list.filter(
        (listItem) =>
          !parsedOptions.some(
            (optionItem) => optionItem.value === listItem.value
          )
      );

      return filteredList;
    };

    // Add the search input inside the options modal
    useEffect(() => {
      if (isOpen && comboboxRef) {
        const newChild = document.createElement('div');
        newChild.className = 'sticky -top-2 z-10 bg-white';
        const firstChild = comboboxRef.firstChild;
        comboboxRef.insertBefore(newChild, firstChild);
        const root = createRoot(newChild);
        root.render(
          <SearchContainer
            handleQuery={handleQuery}
            showSearchIcon={showSearchIcon}
          />
        );
      }
    }, [comboboxRef, isOpen]);

    const filteredSelectedList = useMemo(() => {
      return selectedList.filter(
        (item) => item.value !== 'selectAll' && item.name !== 'selectAll'
      );
    }, [selectedList]);

    const virtualizerOptions = useMemo(
      () => ({
        count: filteredSelectedList?.length ?? 0,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 35,
        overscan: 5,
      }),
      [filteredSelectedList.length]
    );

    // Add virtualizer for dropdown options to improve performance
    const dropdownVirtualizerOptions = useMemo(
      () => ({
        count: parsedOptions?.length ?? 0,
        getScrollElement: () => comboboxRef as HTMLElement,
        estimateSize: () => (renderCustomOption ? 56 : 40),
        overscan: 5,
      }),
      [parsedOptions?.length, comboboxRef, renderCustomOption]
    );

    const comboboxValue = multiple ? selectedList : selectedList[0] || null;

    const rowVirtualizer = useVirtualizer(virtualizerOptions);
    const dropdownVirtualizer = useVirtualizer(dropdownVirtualizerOptions);

    // Infinite scrolling logic
    const fetchMoreOnBottomReached = useCallback(
      (containerRefElement?: HTMLElement | null) => {
        if (containerRefElement && fetchNextPage && hasNextPage) {
          const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
          if (
            scrollHeight - scrollTop - clientHeight < 100 &&
            !isFetching &&
            !isFetchingNextPage
          ) {
            void fetchNextPage();
          }
        }
      },
      [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]
    );

    return (
      <div
        ref={dropdownRef}
        className={`${removeRelativeContainer ? '' : 'relative'} flex-1`}
      >
        <Combobox
          value={comboboxValue}
          by={compareValues}
          onChange={handleSelect}
          multiple={multiple}
          disabled={disabled}
          onClose={() => {
            handleQuery('');
          }}
        >
          <div className="w-full">
            <ComboboxButton
              id={id}
              data-testid={id && `${id}-button`}
              className={`relative w-full flex flex-1 items-center justify-between self-stretch px-3 py-2 border rounded-lg h-11 focus-within:border-primary-light ${
                error ? addInputErrorClasses(error) : 'border-gray-30'
              } ${disabled ? 'bg-gray-25' : 'bg-white'}
 `}
              onClick={(event: any) => {
                if (isOpen && comboboxRef) {
                  // Reset scroll position when closing dropdown
                  const scrollElement = comboboxRef as HTMLElement;
                  if (scrollElement) {
                    requestAnimationFrame(() => {
                      scrollElement.scrollTop = 0;
                    });
                  }
                }
                setIsOpen(!isOpen);
              }}
            >
              <div>
                {!selectedList.length && (
                  <span className="text-gray-400">{placeholder}</span>
                )}
                {selectedList.length > 0 && (
                  <span>
                    {multiple
                      ? // Multi-selection display
                        `Selected (${
                          areAllSelected
                            ? selectedList.length - 1
                            : selectedList.length
                        })`
                      : // Single selection display
                        selectedList[0]?.name}
                  </span>
                )}
              </div>

              {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </ComboboxButton>

            <div>
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
                show={isOpen}
              >
                <div
                  className="absolute left-0 w-full rounded-md py-1 bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none phone:text-sm z-10"
                  style={{ maxHeight: `${maxHeight}px` }}
                >
                  <ComboboxOptions
                    static
                    data-testid={id && `${id}-ul`}
                    className={'mt-1 bg-white w-full overflow-auto py-1'}
                    style={{ maxHeight: `${maxHeight - 8}px` }}
                    ref={setComboboxRef}
                    as={'ul'}
                    onScroll={(e) => {
                      fetchMoreOnBottomReached(e.target as HTMLElement);
                    }}
                  >
                    {!parsedOptions || parsedOptions.length === 0 ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        {isFetching
                          ? t('common.inputs.searching')
                          : t('common.errors.nothingFound')}
                      </div>
                    ) : (
                      <div
                        style={{
                          height: `${dropdownVirtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {dropdownVirtualizer
                          .getVirtualItems()
                          .map((virtualRow) => {
                            const option = parsedOptions[virtualRow.index];
                            if (!option) return null;

                            return (
                              <ComboboxOption
                                key={`${option?.value}-${virtualRow.index}`}
                                className={({ focus, selected }) =>
                                  `absolute left-0 w-full cursor-pointer select-none truncate py-2 pl-10 pr-4 ${
                                    focus || selected
                                      ? 'bg-primary-lighter font-bold text-gray-60'
                                      : 'text-gray-50 bg-white'
                                  }`
                                }
                                style={{
                                  height: `${virtualRow.size}px`,
                                  transform: `translateY(${virtualRow.start}px)`,
                                }}
                                value={option}
                                as={'li'}
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {renderCustomOption
                                        ? renderCustomOption(option)
                                        : option.name}
                                    </span>

                                    {/* Change checkbox icon if the option is selected */}
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <IoIosCheckbox
                                          className="h-5 w-5"
                                          data-testid="checked-item-combobox"
                                          color={theme.colors.primary}
                                        />
                                      </span>
                                    ) : (
                                      // Default empty checkbox icon
                                      <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <MdCheckBoxOutlineBlank
                                          color="black"
                                          className="h-5 w-5"
                                        />
                                      </span>
                                    )}
                                  </>
                                )}
                              </ComboboxOption>
                            );
                          })}
                      </div>
                    )}
                    {/* Loading indicator for infinite scroll */}
                    {isFetchingNextPage && (
                      <div className="flex items-center gap-4 px-4 py-2">
                        <LoadingSpinner />
                        <span>{t('common.inputs.searching')}</span>
                      </div>
                    )}
                  </ComboboxOptions>
                </div>
              </Transition>
            </div>
          </div>
          <FErrorLabel message={error?.message} />
          {multiple && !disableSelectedList && (
            <SelectedOptionsContainer
              className={selectedOptionsContainerClasses ?? ''}
              data-testid={`${testid ?? 'combobox'}-list`}
              ref={parentRef}
              style={{ maxHeight: 300 }}
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow, index) => {
                  const item = filteredSelectedList[virtualRow.index];

                  return (
                    <SelectedItem
                      key={item.value}
                      $index={index}
                      $length={selectedList.length}
                      style={{
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <span> {item.name}</span>
                      {!disabled && (
                        <IoIosClose
                          size={22}
                          className="cursor-pointer text-primary"
                          onClick={() => {
                            handleRemoveFromSelectedList(item.value);
                          }}
                          data-testid={`remove-option-${index + 1}`}
                        />
                      )}
                    </SelectedItem>
                  );
                })}
              </div>
            </SelectedOptionsContainer>
          )}
        </Combobox>
      </div>
    );
  }
);

export default MultiComboBoxInput;
